import React from 'react';
import {
  Dialog, DialogContent, Box, Typography, Grid, TextField, MenuItem,
  Button, IconButton, Divider, FormControl, InputLabel, Select, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { ModuleField } from '../../types';
import { RADIUS, brandColors, brandCss, BASE_FONT_SIZE } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';
import { SearchableEntitySelect, EntityOption } from './SearchableEntitySelect';
import { IndiaLocationSelect } from './IndiaLocationSelect';
import { ImageUploadField } from '../ui/ImageUploadField';
import { FileUploadField } from '../ui/FileUploadField';
import { isFieldVisible } from '../../utils/fieldVisibility';

export interface VehicleFleetSelectors {
  typeOptions: EntityOption[];
  makesForType: (typeId: string) => EntityOption[];
  modelsForMake: (makeId: string, typeId?: string) => EntityOption[];
  loading?: boolean;
}

interface StylishFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  title: string;
  subtitle?: string;
  fields: ModuleField[];
  formData: Record<string, string>;
  onChange: (data: Record<string, string>) => void;
  isEdit?: boolean;
  loading?: boolean;
  clientOptions?: EntityOption[];
  branchOptions?: EntityOption[];
  vehicleOptions?: EntityOption[];
  driverOptions?: EntityOption[];
  helperOptions?: EntityOption[];
  userOptions?: EntityOption[];
  vendorOptions?: EntityOption[];
  vehicleFleet?: VehicleFleetSelectors;
  onImageUpload?: (file: File, fieldName: string) => Promise<string>;
  extraContent?: (formData: Record<string, string>) => React.ReactNode;
}

const sectionTitle = (label: string) => (
  <Box sx={{
    px: { xs: 2, sm: 3 },
    py: 1.75,
    bgcolor: '#f8fafc',
    borderTop: `1px solid ${brandColors.border}`,
    borderBottom: `1px solid ${brandColors.border}`,
  }}>
    <Typography fontWeight={700} sx={{ color: brandCss.primaryDark, fontSize: BASE_FONT_SIZE }}>
      {label}
    </Typography>
  </Box>
);

export const StylishFormModal: React.FC<StylishFormModalProps> = ({
  open, onClose, onSubmit, title, subtitle, fields, formData, onChange, isEdit, loading,
  clientOptions = [], branchOptions = [], vehicleOptions = [], driverOptions = [], helperOptions = [],
  userOptions = [], vendorOptions = [], vehicleFleet, onImageUpload, extraContent,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const set = (name: string, val: string) => {
    const field = fields.find((f) => f.name === name);
    const nextVal = field?.uppercase ? val.toUpperCase() : val;
    if (name === 'typeId') {
      onChange({ ...formData, typeId: nextVal, makeId: '', modelId: '' });
      return;
    }
    if (name === 'makeId') {
      onChange({ ...formData, makeId: nextVal, modelId: '' });
      return;
    }
    if (name === 'vendorId') {
      const vendor = vendorOptions.find((v) => v._id === nextVal);
      onChange({
        ...formData,
        vendorId: nextVal,
        vendorName: vendor?.label || '',
      });
      return;
    }
    if (name === 'ownerType' && nextVal === 'owned') {
      onChange({
        ...formData,
        ownerType: nextVal,
        vendorId: '',
        vendorStartDate: '',
        vendorEndDate: '',
        vendorName: '',
      });
      return;
    }
    if (name === 'paymentType') {
      onChange({
        ...formData,
        paymentType: nextVal,
        monthlySalary: nextVal === 'monthly_salary' ? formData.monthlySalary : '',
        dailyWage: nextVal === 'daily_wage' ? formData.dailyWage : '',
        tripCommission: nextVal === 'trip_commission' ? formData.tripCommission : '',
      });
      return;
    }
    onChange({ ...formData, [name]: nextVal });
  };
  const visible = fields.filter((f) => isFieldVisible(f, formData));

  const groups: { title: string; fields: ModuleField[] }[] = [];
  visible.forEach((field) => {
    const titleLabel = field.section
      || (field.name === 'status' ? 'Active or Inactive' : null)
      || (field.type === 'image' || field.type === 'file' ? 'Documents & Photos' : null)
      || 'General Information';
    const last = groups[groups.length - 1];
    if (last && last.title === titleLabel) {
      last.fields.push(field);
    } else {
      groups.push({ title: titleLabel, fields: [field] });
    }
  });

  const gridWidth = (field: ModuleField) => {
    if (field.fullWidth || field.type === 'textarea' || field.type === 'image' || field.type === 'file') return 12;
    return 6;
  };

  const renderField = (field: ModuleField) => (
    <Grid item xs={12} sm={gridWidth(field)} key={field.name}>
      {field.type === 'client' ? (
        <SearchableEntitySelect
          label={field.label}
          placeholder={field.placeholder}
          value={formData[field.name] || ''}
          options={clientOptions}
          onChange={(v) => set(field.name, v)}
          required={field.required}
        />
      ) : field.type === 'branch' ? (
        <SearchableEntitySelect
          label={field.label}
          placeholder={field.placeholder || 'Select branch...'}
          value={formData[field.name] || ''}
          options={branchOptions}
          onChange={(v) => set(field.name, v)}
          required={field.required}
        />
      ) : field.type === 'vehicle' ? (
        <SearchableEntitySelect
          label={field.label}
          placeholder={field.placeholder || 'Select vehicle...'}
          value={formData[field.name] || ''}
          options={vehicleOptions}
          onChange={(v) => set(field.name, v)}
          required={field.required}
        />
      ) : field.type === 'driver' ? (
        <SearchableEntitySelect
          label={field.label}
          placeholder={field.placeholder || 'Select driver...'}
          value={formData[field.name] || ''}
          options={driverOptions}
          onChange={(v) => set(field.name, v)}
          required={field.required}
        />
      ) : field.type === 'helper' ? (
        <SearchableEntitySelect
          label={field.label}
          placeholder={field.placeholder || 'Select helper...'}
          value={formData[field.name] || ''}
          options={helperOptions}
          onChange={(v) => set(field.name, v)}
          required={field.required}
        />
      ) : field.type === 'vehicle_type' ? (
        <SearchableEntitySelect
          label={field.label}
          placeholder={field.placeholder || 'Select vehicle type...'}
          value={formData[field.name] || ''}
          options={vehicleFleet?.typeOptions || []}
          onChange={(v) => set(field.name, v)}
          required={field.required}
        />
      ) : field.type === 'vehicle_make' ? (
        <SearchableEntitySelect
          label={field.label}
          placeholder={field.placeholder || 'Select make...'}
          value={formData[field.name] || ''}
          options={vehicleFleet?.makesForType(formData.typeId || '') || []}
          onChange={(v) => set(field.name, v)}
          required={field.required}
        />
      ) : field.type === 'vehicle_model' ? (
        <SearchableEntitySelect
          label={field.label}
          placeholder={formData.makeId ? (field.placeholder || 'Select model...') : 'Select make first'}
          value={formData[field.name] || ''}
          options={vehicleFleet?.modelsForMake(formData.makeId || '', formData.typeId || '') || []}
          onChange={(v) => set(field.name, v)}
          required={field.required}
        />
      ) : field.type === 'user' ? (
        <SearchableEntitySelect
          label={field.label}
          placeholder={field.placeholder || 'Select employee...'}
          value={formData[field.name] || ''}
          options={userOptions}
          onChange={(v) => set(field.name, v)}
          required={field.required}
        />
      ) : field.type === 'vendor' ? (
        <SearchableEntitySelect
          label={field.label}
          placeholder={field.placeholder || 'Choose vehicle vendor...'}
          value={formData[field.name] || ''}
          options={vendorOptions}
          onChange={(v) => set(field.name, v)}
          required={field.required}
        />
      ) : field.type === 'location' ? (
        <IndiaLocationSelect
          label={field.label}
          placeholder={field.placeholder}
          value={formData[field.name] || ''}
          onChange={(v) => set(field.name, v)}
          required={field.required}
        />
      ) : field.type === 'file' ? (
        onImageUpload ? (
          <FileUploadField
            label={field.label}
            value={formData[field.name]}
            onUpload={(file) => onImageUpload(file, field.name)}
            onChange={(url) => set(field.name, url)}
            helperText={field.placeholder || `Upload PDF or Image (Max ${field.maxSizeMB || 2}MB)`}
            accept={field.accept}
            maxSizeMB={field.maxSizeMB || 2}
            required={field.required}
          />
        ) : (
          <TextField label={field.label} value={formData[field.name] || ''} fullWidth size="small" disabled helperText="Upload not configured" />
        )
      ) : field.type === 'image' ? (
        onImageUpload ? (
          <ImageUploadField
            label={field.label}
            value={formData[field.name]}
            onUpload={(file) => onImageUpload(file, field.name)}
            onChange={(url) => set(field.name, url)}
            helperText={formData[field.name] ? 'Image uploaded' : 'No file chosen'}
          />
        ) : (
          <TextField label={field.label} value={formData[field.name] || ''} fullWidth size="small" disabled helperText="Upload not configured" />
        )
      ) : field.type === 'select' ? (
        <FormControl fullWidth size="small" required={field.required} sx={catchyTextFieldSx}>
          <InputLabel shrink>{field.label}</InputLabel>
          <Select
            label={field.label}
            notched={false}
            displayEmpty
            value={formData[field.name] || ''}
            onChange={(e) => set(field.name, e.target.value)}
          >
            {field.placeholder && !formData[field.name] ? (
              <MenuItem value="" disabled>
                <em>{field.placeholder}</em>
              </MenuItem>
            ) : null}
            {(field.options || []).map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <TextField
          label={field.label}
          placeholder={field.placeholder}
          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'datetime' ? 'datetime-local' : field.type === 'url' ? 'url' : 'text'}
          multiline={field.type === 'textarea'}
          rows={field.type === 'textarea' ? 3 : undefined}
          value={formData[field.name] || ''}
          onChange={(e) => set(field.name, e.target.value)}
          required={field.required}
          fullWidth
          size="small"
          sx={catchyTextFieldSx}
          inputProps={field.uppercase ? { style: { textTransform: 'uppercase' } } : undefined}
          InputLabelProps={{ shrink: true }}
        />
      )}
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : `${RADIUS}px`, overflow: 'hidden', maxHeight: fullScreen ? '100%' : '90vh' } }}>
      <Box sx={{
        background: brandCss.formHeaderGradient,
        px: { xs: 2, sm: 3 }, py: 2.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      }}>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: brandCss.formHeaderText, fontSize: { xs: 16, sm: 17 } }}>
            {isEdit ? 'Edit' : 'Add New'} {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ color: brandCss.formHeaderText, opacity: 0.85, mt: 0.5, display: 'block', fontSize: BASE_FONT_SIZE }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ color: brandCss.formHeaderText, opacity: 0.85, mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {groups.map((group, idx) => (
          <React.Fragment key={`${group.title}-${idx}`}>
            {sectionTitle(group.title)}
            <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
              <Grid container spacing={{ xs: 2, sm: 2.5 }}>
                {group.fields.map(renderField)}
              </Grid>
            </Box>
          </React.Fragment>
        ))}

        {extraContent && (
          <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2.5 }}>
            {extraContent(formData)}
          </Box>
        )}

        <Divider />

        <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5, bgcolor: '#f8fafc', flexWrap: 'wrap' }}>
          <Button onClick={onClose} variant="outlined"
            sx={{ borderColor: brandColors.border, color: brandColors.textMuted, px: 3, borderRadius: `${RADIUS}px`, fontSize: BASE_FONT_SIZE, flex: { xs: '1 1 auto', sm: '0 0 auto' } }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSubmit} disabled={loading} startIcon={<SaveIcon />}
            sx={{ ...catchyPrimaryButtonSx, px: 4, flex: { xs: '1 1 auto', sm: '0 0 auto' } }}>
            {isEdit ? 'Update Record' : 'Save Record'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
